// Enhanced AI Citation & Traffic Analytics
// Tracks visits from AI assistants, monitors brand mentions, and prepares for visibility tools

// Known AI referrer patterns
const AI_REFERRERS = {
  'chat.openai.com': 'ChatGPT',
  'chatgpt.com': 'ChatGPT',
  'perplexity.ai': 'Perplexity',
  'claude.ai': 'Claude',
  'anthropic.com': 'Claude',
  'bard.google.com': 'Google Bard',
  'gemini.google.com': 'Google Gemini',
  'copilot.microsoft.com': 'Microsoft Copilot',
  'bing.com/chat': 'Bing Chat',
  'you.com': 'You.com',
  'phind.com': 'Phind',
  'poe.com': 'Poe',
  'meta.ai': 'Meta AI',
  'character.ai': 'Character AI',
  'brave.com/search': 'Brave AI',
  'duckduckgo.com': 'DuckDuckGo AI'
};

// AI bot user agent patterns
const AI_USER_AGENTS = [
  { pattern: /GPTBot/i, name: 'GPTBot', provider: 'OpenAI' },
  { pattern: /ChatGPT-User/i, name: 'ChatGPT-User', provider: 'OpenAI' },
  { pattern: /ClaudeBot/i, name: 'ClaudeBot', provider: 'Anthropic' },
  { pattern: /Claude-Web/i, name: 'Claude-Web', provider: 'Anthropic' },
  { pattern: /anthropic-ai/i, name: 'Anthropic', provider: 'Anthropic' },
  { pattern: /PerplexityBot/i, name: 'PerplexityBot', provider: 'Perplexity' },
  { pattern: /Google-Extended/i, name: 'Google-Extended', provider: 'Google' },
  { pattern: /Googlebot/i, name: 'Googlebot', provider: 'Google' },
  { pattern: /Bingbot/i, name: 'Bingbot', provider: 'Microsoft' },
  { pattern: /cohere-ai/i, name: 'Cohere', provider: 'Cohere' },
  { pattern: /Amazonbot/i, name: 'Amazonbot', provider: 'Amazon' },
  { pattern: /CCBot/i, name: 'CommonCrawl', provider: 'CommonCrawl' },
  { pattern: /YouBot/i, name: 'YouBot', provider: 'You.com' },
  { pattern: /BraveBot/i, name: 'BraveBot', provider: 'Brave' },
  { pattern: /DuckDuckBot/i, name: 'DuckDuckBot', provider: 'DuckDuckGo' },
  { pattern: /Applebot/i, name: 'Applebot', provider: 'Apple' }
];

// UTM parameters for AI traffic
const AI_UTM_SOURCES = ['chatgpt', 'perplexity', 'claude', 'bard', 'gemini', 'copilot', 'ai', 'llm', 'aiassistant'];

// Brand facts URL for verification
const BRAND_FACTS_URL = '/brand-facts.json';

/**
 * AI Visibility Tracking Configuration
 * Prepare hooks for external tools like Siftly, SE Ranking, Profound AI
 */
const AI_VISIBILITY_CONFIG = {
  // Webhook endpoints for external tools (configure when subscribed)
  webhooks: {
    siftly: null, // Set to Siftly webhook URL when subscribed
    seRanking: null, // Set to SE Ranking webhook URL when subscribed
    profoundAI: null, // Set to Profound AI webhook URL when subscribed
    custom: null // Custom analytics endpoint
  },
  // Brand terms to monitor for "Share of Voice"
  brandTerms: [
    'modular furniture store',
    'mfs',
    'modular furniture mumbai',
    'modular workstation mumbai',
    'office furniture mumbai'
  ],
  // Competitor terms to track
  competitors: [
    'godrej interio',
    'featherlite',
    'steelcase',
    'herman miller',
    'wipro furniture'
  ]
};

class AICitationTracker {
  constructor() {
    this.sessionData = {
      sessionId: this.generateSessionId(),
      source: null,
      referrer: document.referrer,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      isAITraffic: false,
      aiSource: null,
      aiProvider: null,
      landingPage: window.location.pathname,
      utmParams: this.extractUTMParams(),
      deviceType: this.detectDeviceType(),
      brandFactsLoaded: false
    };
    
    this.conversionFunnel = [];
    this.init();
  }

  generateSessionId() {
    return `ai_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  extractUTMParams() {
    const params = new URLSearchParams(window.location.search);
    return {
      source: params.get('utm_source'),
      medium: params.get('utm_medium'),
      campaign: params.get('utm_campaign'),
      content: params.get('utm_content'),
      term: params.get('utm_term'),
      aiRef: params.get('ai_ref'), // Custom AI reference parameter
      aiQuery: params.get('ai_query') // The query that led here from AI
    };
  }

  detectDeviceType() {
    const ua = navigator.userAgent;
    if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet';
    if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return 'mobile';
    return 'desktop';
  }

  init() {
    this.detectAISource();
    this.loadBrandFacts();
    this.trackPageView();
    this.setupEventListeners();
    this.setupPerformanceTracking();
  }

  // Load brand facts to ensure they're cached
  async loadBrandFacts() {
    try {
      const response = await fetch(BRAND_FACTS_URL);
      if (response.ok) {
        this.sessionData.brandFactsLoaded = true;
        // Cache brand facts for quick access
        const facts = await response.json();
        sessionStorage.setItem('brand_facts', JSON.stringify(facts));
      }
    } catch (e) {
      console.warn('Failed to load brand facts:', e);
    }
  }

  // Detect if traffic is from AI source
  detectAISource() {
    const referrer = document.referrer.toLowerCase();
    const { source: utmSource, medium: utmMedium, aiRef } = this.sessionData.utmParams;

    // Check for explicit AI reference parameter
    if (aiRef) {
      this.sessionData.isAITraffic = true;
      this.sessionData.aiSource = aiRef;
      this.sessionData.source = 'ai_ref_param';
      return;
    }

    // Check referrer
    for (const [domain, name] of Object.entries(AI_REFERRERS)) {
      if (referrer.includes(domain)) {
        this.sessionData.isAITraffic = true;
        this.sessionData.aiSource = name;
        this.sessionData.source = 'referrer';
        return;
      }
    }

    // Check UTM parameters
    if (utmSource && AI_UTM_SOURCES.some(s => utmSource.toLowerCase().includes(s))) {
      this.sessionData.isAITraffic = true;
      this.sessionData.aiSource = utmSource;
      this.sessionData.source = 'utm';
      return;
    }

    if (utmMedium === 'ai' || utmMedium === 'ai-search' || utmMedium === 'llm') {
      this.sessionData.isAITraffic = true;
      this.sessionData.aiSource = utmSource || 'ai';
      this.sessionData.source = 'utm';
      return;
    }

    // Check user agent (for bots)
    for (const { pattern, name, provider } of AI_USER_AGENTS) {
      if (pattern.test(navigator.userAgent)) {
        this.sessionData.isAITraffic = true;
        this.sessionData.aiSource = name;
        this.sessionData.aiProvider = provider;
        this.sessionData.source = 'user-agent';
        return;
      }
    }
  }

  // Track page view with enhanced data
  trackPageView() {
    const pageData = {
      page: window.location.pathname,
      title: document.title,
      fullUrl: window.location.href,
      ...this.sessionData
    };

    // Send to Google Analytics if available
    if (window.gtag) {
      // Standard page view
      window.gtag('event', 'page_view', {
        page_path: window.location.pathname,
        page_title: document.title
      });

      // AI-specific tracking
      if (this.sessionData.isAITraffic) {
        window.gtag('event', 'ai_traffic', {
          'event_category': 'AI Citation',
          'event_label': this.sessionData.aiSource,
          'ai_source': this.sessionData.aiSource,
          'ai_provider': this.sessionData.aiProvider,
          'referrer': document.referrer,
          'page_path': window.location.pathname,
          'session_id': this.sessionData.sessionId
        });

        // Track as a dimension for reporting
        window.gtag('set', 'user_properties', {
          'ai_visitor': 'true',
          'ai_source': this.sessionData.aiSource
        });
      }
    }

    // Send to external visibility tools if configured
    this.sendToVisibilityTools(pageData);

    // Store in localStorage for analytics
    this.storeAnalytics(pageData);

    // Log in development
    if (process.env.NODE_ENV === 'development') {
      console.log('AI Citation Tracker:', pageData);
    }
  }

  // Send data to external AI visibility tools
  async sendToVisibilityTools(data) {
    const { webhooks } = AI_VISIBILITY_CONFIG;
    
    const payload = {
      ...data,
      brandTerms: AI_VISIBILITY_CONFIG.brandTerms,
      timestamp: new Date().toISOString()
    };

    // Send to each configured webhook
    for (const [tool, url] of Object.entries(webhooks)) {
      if (url) {
        try {
          await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ tool, ...payload })
          });
        } catch (e) {
          console.warn(`Failed to send to ${tool}:`, e);
        }
      }
    }
  }

  // Store analytics data with enhanced structure
  storeAnalytics(data) {
    try {
      const stored = JSON.parse(localStorage.getItem('ai_analytics') || '[]');
      stored.push({
        ...data,
        storedAt: new Date().toISOString()
      });
      // Keep last 500 entries
      while (stored.length > 500) stored.shift();
      localStorage.setItem('ai_analytics', JSON.stringify(stored));
    } catch (e) {
      console.error('Failed to store AI analytics:', e);
    }
  }

  // Setup event listeners for tracking
  setupEventListeners() {
    // Track all interactions for AI traffic
    document.addEventListener('click', (e) => {
      const button = e.target.closest('button');
      const link = e.target.closest('a');
      
      if (button) {
        const text = button.textContent?.trim();
        if (text?.includes('ADD TO CART')) {
          this.trackFunnelEvent('add_to_cart', { button: text });
        } else if (text?.includes('PLACE ORDER') || text?.includes('CHECKOUT')) {
          this.trackFunnelEvent('checkout_initiated', { button: text });
        } else if (text?.includes('PAY')) {
          this.trackFunnelEvent('payment_initiated', { button: text });
        }
      }

      if (link) {
        const href = link.getAttribute('href');
        if (href?.includes('/products/')) {
          this.trackFunnelEvent('product_viewed', { url: href });
        } else if (href?.includes('/contact')) {
          this.trackFunnelEvent('contact_intent', { url: href });
        }
      }
    });

    // Track form submissions
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form.tagName === 'FORM') {
        this.trackFunnelEvent('form_submitted', { formId: form.id || 'unknown' });
      }
    });

    // Track scroll depth
    let maxScroll = 0;
    window.addEventListener('scroll', () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100
      );
      if (scrollPercent > maxScroll) {
        maxScroll = scrollPercent;
        if ([25, 50, 75, 100].includes(scrollPercent)) {
          this.trackFunnelEvent('scroll_depth', { percent: scrollPercent });
        }
      }
    }, { passive: true });
  }

  // Setup performance tracking
  setupPerformanceTracking() {
    if (window.PerformanceObserver) {
      // Track Core Web Vitals for AI traffic analysis
      try {
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (this.sessionData.isAITraffic && window.gtag) {
              window.gtag('event', 'web_vitals', {
                event_category: 'Performance',
                event_label: entry.name,
                value: Math.round(entry.value),
                ai_traffic: true
              });
            }
          }
        });
        observer.observe({ entryTypes: ['largest-contentful-paint', 'first-input', 'layout-shift'] });
      } catch (e) {
        // Performance observer not supported
      }
    }
  }

  // Track funnel events
  trackFunnelEvent(action, metadata = {}) {
    const event = {
      action,
      metadata,
      timestamp: new Date().toISOString(),
      page: window.location.pathname,
      isAITraffic: this.sessionData.isAITraffic,
      aiSource: this.sessionData.aiSource
    };

    this.conversionFunnel.push(event);

    if (window.gtag) {
      window.gtag('event', this.sessionData.isAITraffic ? `ai_${action}` : action, {
        'event_category': this.sessionData.isAITraffic ? 'AI Conversion' : 'Conversion',
        'event_label': this.sessionData.aiSource || 'direct',
        ...metadata
      });
    }

    // Store funnel data
    try {
      const funnel = JSON.parse(localStorage.getItem('conversion_funnel') || '[]');
      funnel.push(event);
      while (funnel.length > 200) funnel.shift();
      localStorage.setItem('conversion_funnel', JSON.stringify(funnel));
    } catch (e) {
      console.error('Failed to store funnel event:', e);
    }
  }

  // Get comprehensive analytics summary
  static getAnalyticsSummary() {
    try {
      const stored = JSON.parse(localStorage.getItem('ai_analytics') || '[]');
      const funnel = JSON.parse(localStorage.getItem('conversion_funnel') || '[]');
      const aiVisits = stored.filter(s => s.isAITraffic);
      
      const summary = {
        totalVisits: stored.length,
        aiVisits: aiVisits.length,
        aiPercentage: stored.length > 0 ? ((aiVisits.length / stored.length) * 100).toFixed(2) : 0,
        bySource: {},
        byProvider: {},
        byLandingPage: {},
        conversionFunnel: {
          totalEvents: funnel.length,
          aiEvents: funnel.filter(e => e.isAITraffic).length,
          byAction: {}
        },
        timeRange: {
          earliest: stored[0]?.timestamp,
          latest: stored[stored.length - 1]?.timestamp
        }
      };

      // Aggregate by source
      aiVisits.forEach(visit => {
        const source = visit.aiSource || 'unknown';
        summary.bySource[source] = (summary.bySource[source] || 0) + 1;
        
        const provider = visit.aiProvider || 'unknown';
        summary.byProvider[provider] = (summary.byProvider[provider] || 0) + 1;

        const page = visit.landingPage || visit.page;
        summary.byLandingPage[page] = (summary.byLandingPage[page] || 0) + 1;
      });

      // Aggregate funnel events
      funnel.forEach(event => {
        summary.conversionFunnel.byAction[event.action] = 
          (summary.conversionFunnel.byAction[event.action] || 0) + 1;
      });

      return summary;
    } catch (e) {
      return { error: e.message };
    }
  }

  // Get AI Share of Voice data (placeholder for external tools)
  static getShareOfVoice() {
    return {
      message: 'AI Share of Voice tracking requires external tools',
      recommendedTools: ['Siftly', 'SE Ranking AI Toolkit', 'Profound AI'],
      brandTerms: AI_VISIBILITY_CONFIG.brandTerms,
      competitors: AI_VISIBILITY_CONFIG.competitors,
      setupInstructions: 'Configure webhook URLs in AI_VISIBILITY_CONFIG.webhooks'
    };
  }

  // Export data for external analysis
  static exportData(format = 'json') {
    const data = {
      analytics: JSON.parse(localStorage.getItem('ai_analytics') || '[]'),
      funnel: JSON.parse(localStorage.getItem('conversion_funnel') || '[]'),
      summary: AICitationTracker.getAnalyticsSummary()
    };

    if (format === 'csv') {
      // Convert to CSV
      const rows = data.analytics.map(item => 
        Object.values(item).map(v => `"${v}"`).join(',')
      );
      const headers = Object.keys(data.analytics[0] || {}).join(',');
      return `${headers}\n${rows.join('\n')}`;
    }

    return JSON.stringify(data, null, 2);
  }

  // Clear all analytics data
  static clearAnalytics() {
    localStorage.removeItem('ai_analytics');
    localStorage.removeItem('conversion_funnel');
    sessionStorage.removeItem('brand_facts');
  }
}

// Initialize tracker
let tracker = null;

export function initAICitationTracker() {
  if (typeof window !== 'undefined' && !tracker) {
    tracker = new AICitationTracker();
  }
  return tracker;
}

export function getAIAnalytics() {
  return AICitationTracker.getAnalyticsSummary();
}

export function getShareOfVoice() {
  return AICitationTracker.getShareOfVoice();
}

export function exportAIData(format) {
  return AICitationTracker.exportData(format);
}

export function isAITraffic() {
  return tracker?.sessionData?.isAITraffic || false;
}

export function getAISource() {
  return tracker?.sessionData?.aiSource || null;
}

export function getSessionData() {
  return tracker?.sessionData || null;
}

export { AI_VISIBILITY_CONFIG };

export default AICitationTracker;
